import { useMemo, useCallback, useState, useRef } from 'react'
import { fileKind, type FileKind } from '@itphx/synth-shared/types'
import { cn } from '@/lib/utils'
import { Icon } from '../icon/Icon'
import { useI18n } from '@/lib/i18n'
import { useUIStore } from '../../stores/useUIStore'
import { useProjectsStore } from '../../stores/projectsStore'
import { useChatStore } from '@/stores/chatStore'
import { applyHistoryPage } from '@/stores/transportStore'
import { useSessionsStore } from '@/stores/sessionsStore'
import { synthApi } from '@/lib/api/synthApi'
import { MESSAGES_PAGE_LIMIT } from '@/lib/api/messages'
import { useNotification } from '@/hooks/useNotification'
import { useAuthStore } from '@/stores/authStore'
import { confirm } from '@/lib/confirm'
import {
  CONTEXT_LEVEL_CLASSES,
  useContextWindowUsage,
} from '@/hooks/useContextWindowUsage'
import { QuickSettingsPopup } from '../auth/QuickSettingsPopup'
import { AnalyticsButton } from './AnalyticsButton'
import { NotificationPopup } from './NotificationPopup'
import { GlobalTransportIndicator } from './GlobalTransportIndicator'
import { HybridStatusIndicator } from './HybridStatusIndicator'
import { PwaInstallButton } from './PwaInstallButton'
import { ChatViewControls } from './ChatViewControls'
import { useDevice } from '@/hooks/useDevice'
import { resolveIconName, resolveIconColor } from '@/lib/entityIcon'
import { useWorktreesStore, findWorktree } from '@/stores/worktreesStore'

interface HeaderProps {
  onSettingsClick: () => void
}

/** Стабильный пустой набор выделения (иначе новый `Set` ломает мемоизацию). */
const EMPTY_SELECTED_IDS: Set<string> = new Set()

/**
 * Семейства, которые нельзя открыть в текстовом редакторе: медиа и бинарь
 * (просмотр — `FileViewerTab`, скачивание — `/fs/download`).
 */
const NON_EDITABLE_FILE_KINDS: readonly FileKind[] = ['image', 'video', 'audio', 'pdf', 'binary']

/**
 * Имя каталога из абсолютного пути — фолбэк для индикатора worktree'а,
 * когда записи ещё нет в инвентаре проекта (список не загружен).
 */
function pathBasename(path: string): string {
  const parts = path.replace(/[/\\]+$/, '').split(/[/\\]/)
  return parts[parts.length - 1] || path
}

export const Header = ({ onSettingsClick }: HeaderProps) => {
  const { t } = useI18n()
  const { notify } = useNotification()
  const leftPanel = useUIStore((s) => s.leftPanel)
  const rightPanel = useUIStore((s) => s.rightPanel)
  const toggleLeftPanel = useUIStore((s) => s.toggleLeftPanel)
  const toggleRightPanel = useUIStore((s) => s.toggleRightPanel)
  const setSettingsDialogOpen = useUIStore((s) => s.setSettingsDialogOpen)
  const setLogsDialogOpen = useUIStore((s) => s.setLogsDialogOpen)
  const setAnalyticsDialogOpen = useUIStore((s) => s.setAnalyticsDialogOpen)
  const centerTabs = useUIStore((s) => s.centerTabs)
  const activeCenterTabId = useUIStore((s) => s.activeCenterTabId)
  const openTab = useUIStore((s) => s.openTab)
  const closeTab = useUIStore((s) => s.closeTab)
  const mobileLeftOpen = useUIStore((s) => s.mobileLeftOpen)
  const mobileRightOpen = useUIStore((s) => s.mobileRightOpen)
  const setMobileLeftOpen = useUIStore((s) => s.setMobileLeftOpen)
  const setMobileRightOpen = useUIStore((s) => s.setMobileRightOpen)
  const setRightPanelTab = useUIStore((s) => s.setRightPanelTab)
  const user = useAuthStore((s) => s.user)
  const { deviceType } = useDevice()
  const isMobile = deviceType !== 'desktop'
  const projects = useProjectsStore((s) => s.projects)

  const activeTab = centerTabs.find((tab) => tab.id === activeCenterTabId)

  const headerProject = useMemo(
    () => (activeTab?.data.projectId ? projects.find((p) => p.id === activeTab.data.projectId) : undefined),
    [activeTab, projects],
  )
  const headerTitle = headerProject?.name || 'Synth'
  const headerIcon = resolveIconName(headerProject?.icon) ?? (headerProject ? 'folder-3' : undefined)
  const headerIconColor = resolveIconColor(headerProject?.color)

  const isChatTab = activeTab?.type === 'chat'
  const isFileViewer = activeTab?.type === 'file-viewer'
  const isFileEditor = activeTab?.type === 'file-editor'
  const isCommitDetail = activeTab?.type === 'commit-detail'
  // «Редактировать» предлагаем только для текста/кода/markdown: медиа и бинарь
  // в текстовом редакторе бессмысленны (только просмотр/скачивание).
  const canEditViewedFile =
    isFileViewer && activeTab?.data.filePath
      ? !NON_EDITABLE_FILE_KINDS.includes(fileKind(activeTab.data.filePath))
      : false

  // Процент контекстного окна — из общего хука (тот же, что в правой панели).
  const sessionId = activeTab?.data?.sessionId
  const sessions = useSessionsStore((s) => s.sessions)
  const activeSession = sessionId ? sessions.find((s) => s.id === sessionId) : undefined

  const {
    inputTokens: contextInputTokens,
    windowTokens: contextWindowTokens,
    percent: windowPercent,
    isEstimated: contextEstimated,
    level: contextLevel,
  } = useContextWindowUsage(sessionId)
  const contextLevelClasses = CONTEXT_LEVEL_CLASSES[contextLevel]
  // `≈` — окно модели не задано, используется оценка (фоллбэк 200k).
  const contextTitle = `${contextInputTokens.toLocaleString()} / ${contextEstimated ? '≈' : ''}${contextWindowTokens.toLocaleString()} (${windowPercent}%)`

  // ADR-114 §9: индикатор привязки сессии к worktree'у. Имя — из инвентаря
  // проекта (по `worktree_id`), фолбэк — имя каталога на диске.
  const worktreesByProject = useWorktreesStore((s) => s.byProject)
  const sessionWorktree = activeSession?.worktree ?? null
  const worktreeName = useMemo(() => {
    if (!sessionWorktree) return null
    const record = findWorktree({ byProject: worktreesByProject }, sessionWorktree.worktree_id)
    return record?.name ?? pathBasename(sessionWorktree.directory)
  }, [sessionWorktree, worktreesByProject])

  const handleWorktreeClick = useCallback(() => {
    setRightPanelTab('project')
    if (isMobile) setMobileRightOpen(true)
  }, [setRightPanelTab, setMobileRightOpen, isMobile])

  const [summarizing, setSummarizing] = useState(false)
  const canSummarize = activeSession?.can_summarize ?? false
  const isSubagentSession = activeSession?.session_origin === 'subagent'

  const handleClearSession = useCallback(async () => {
    if (!sessionId) return
    const ok = await confirm({ title: t('leftPanel.confirmClearSession'), variant: 'warning' })
    if (!ok) return
    try {
      await synthApi.clearSession(sessionId)
      useChatStore.getState().clearSession(sessionId)
      const result = await synthApi.getSessionMessages(sessionId, { limit: MESSAGES_PAGE_LIMIT })
      // История очищена — снимок авторитетен, пагинация сбрасывается.
      applyHistoryPage(sessionId, result)
      notify({ text: t('chat.session.cleared'), type: 'success', category: 'queue', sessionId })
    } catch {
      notify({ text: t('chat.error.clearSession'), type: 'error', category: 'error', sessionId })
    }
  }, [sessionId, notify, t])

  const handleSummarize = useCallback(async () => {
    if (!sessionId) return
    setSummarizing(true)
    try {
      await synthApi.summarizeSession(sessionId)
      notify({ text: t('rightPanel.context.summarizeEnqueued'), type: 'info', category: 'queue', sessionId })
    } catch (e) {
      notify({ text: `${t('rightPanel.context.summarizeError')}: ${(e as Error).message}`, type: 'error', category: 'error', sessionId })
    } finally {
      setSummarizing(false)
    }
  }, [sessionId, notify, t])

  const handleFileEdit = useCallback(() => {
    if (!activeTab?.data.projectId || !activeTab?.data.filePath) return
    openTab({
      id: `edit-${activeTab.data.projectId}/${activeTab.data.filePath}`,
      type: 'file-editor',
      title: activeTab.title,
      data: { filePath: activeTab.data.filePath, projectId: activeTab.data.projectId },
    })
  }, [activeTab, openTab])

  const handleFileDownload = useCallback(() => {
    if (!activeTab?.data.projectId || !activeTab?.data.filePath) return
    const url = synthApi.downloadFile(activeTab.data.projectId, activeTab.data.filePath)
    const a = document.createElement('a')
    a.href = url
    a.download = activeTab.title
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }, [activeTab])

  // File editor state (from tab metadata)
  const editingState = activeTab?.editingState
  const isEditorDirty = editingState === 'dirty' || editingState === 'error'
  const isSaving = editingState === 'saving'

  const handleFileSave = useCallback(async () => {
    if (!activeTab?.data.projectId || !activeTab?.data.filePath || !isEditorDirty || isSaving) return
    // Trigger save via custom event – FileEditorTab listens for this
    window.dispatchEvent(new CustomEvent('synth:save-file', { detail: { tabId: activeTab.id } }))
  }, [activeTab, isEditorDirty, isSaving])

  const handleFileClose = useCallback(() => {
    if (activeTab) closeTab(activeTab.id)
  }, [activeTab, closeTab])

  const handleCommitCopy = useCallback(() => {
    if (activeTab?.data.commitHash) {
      navigator.clipboard.writeText(activeTab.data.commitHash)
      notify({ text: t('commit.copied'), type: 'info', category: 'connection' })
    }
  }, [activeTab, t, notify])

  const handleCommitClose = useCallback(() => {
    if (activeTab) closeTab(activeTab.id)
  }, [activeTab, closeTab])

  // ---- Selection mode & bulk operations (from ChatTab) ----
  const selectionState = useChatStore(s => sessionId ? s.selectionBySession[sessionId] : undefined)
  const selectionMode = selectionState?.mode ?? false
  // ADR-118 §7: выделение адресуется по `id` (лента сортируется по `ord`).
  const selectedIds = selectionState?.ids ?? EMPTY_SELECTED_IDS
  const selectedCount = selectedIds.size
  const sessionMessages = sessionId ? useChatStore.getState().messagesBySession[sessionId] || [] : []
  const totalMessageCount = sessionMessages.length

  const handleToggleSelectionMode = useCallback(() => {
    if (!sessionId) return
    useChatStore.getState().toggleSelectionMode(sessionId)
  }, [sessionId])

  const handleSelectAll = useCallback(() => {
    if (!sessionId) return
    useChatStore.getState().selectAllMessages(sessionId)
  }, [sessionId])

  const handleClearSelection = useCallback(() => {
    if (!sessionId) return
    useChatStore.getState().clearSelection(sessionId)
  }, [sessionId])

  const afterBulkReload = useCallback(async () => {
    if (!sessionId) return
    const result = await synthApi.getSessionMessages(sessionId, { limit: MESSAGES_PAGE_LIMIT })
    // Снимок — страница истории: мерж, а не `setMessages` (иначе догруженные
    // старые страницы ленты терялись бы).
    applyHistoryPage(sessionId, result)
    useChatStore.getState().clearSelection(sessionId)
  }, [sessionId])

  const handleBulkPin = useCallback(async () => {
    if (!sessionId) return
    const ids = [...selectedIds]
    if (ids.length === 0) return
    // Determine target state: if ALL are pinned → unpin, else → pin
    const selectedMsgs = sessionMessages.filter(m => m.id && selectedIds.has(m.id))
    const allPinned = selectedMsgs.length > 0 && selectedMsgs.every(m => m.pinned)
    const targetState = !allPinned
    try {
      await synthApi.batchMessages(sessionId, 'pin', ids, targetState)
      await afterBulkReload()
      notify({ text: targetState ? `Закреплено: ${ids.length}` : `Откреплено: ${ids.length}`, type: 'info', category: 'message', sessionId })
    } catch (e) {
      notify({ text: `Ошибка: ${(e as Error).message}`, type: 'error', category: 'error', sessionId })
    }
  }, [sessionId, selectedIds, sessionMessages, afterBulkReload, notify])

  const handleBulkToggle = useCallback(async () => {
    if (!sessionId) return
    const ids = [...selectedIds]
    if (ids.length === 0) return
    // Determine target state: if ALL are disabled → enable, else → disable
    const selectedMsgs = sessionMessages.filter(m => m.id && selectedIds.has(m.id))
    const allDisabled = selectedMsgs.length > 0 && selectedMsgs.every(m => m.disabled)
    const targetState = !allDisabled
    try {
      await synthApi.batchMessages(sessionId, 'toggle', ids, targetState)
      await afterBulkReload()
      notify({ text: targetState ? `Отключено: ${ids.length}` : `Включено: ${ids.length}`, type: 'info', category: 'message', sessionId })
    } catch (e) {
      notify({ text: `Ошибка: ${(e as Error).message}`, type: 'error', category: 'error', sessionId })
    }
  }, [sessionId, selectedIds, sessionMessages, afterBulkReload, notify])

  const handleBulkDelete = useCallback(async () => {
    if (!sessionId) return
    const ids = [...selectedIds]
    if (ids.length === 0) return
    const ok = await confirm({ title: `Удалить ${ids.length} сообщений?`, variant: 'danger' })
    if (!ok) return
    try {
      await synthApi.batchMessages(sessionId, 'delete', ids)
      await afterBulkReload()
      notify({ text: `Удалено сообщений: ${ids.length}`, type: 'info', category: 'message', sessionId })
    } catch (e) {
      notify({ text: `Ошибка: ${(e as Error).message}`, type: 'error', category: 'error', sessionId })
    }
  }, [sessionId, selectedIds, afterBulkReload, notify])

  const handleForward = useCallback(() => {
    if (!sessionId) return
    const ids = [...selectedIds]
    if (ids.length === 0) return
    // Режим переноса адресуется позициями ленты — переводим `id` → `index`.
    const positions = sessionMessages
      .map((m, position) => (m.id && selectedIds.has(m.id) ? position : -1))
      .filter((position) => position >= 0)
    if (positions.length === 0) return
    useUIStore.getState().enterForwardMode(sessionId, positions, 'forward')
  }, [sessionId, selectedIds, sessionMessages])

  const handleMerge = useCallback(() => {
    if (!sessionId) return
    useUIStore.getState().enterForwardMode(sessionId, [], 'merge')
  }, [sessionId])

  // Register forward confirm action
  const forwardCallbackRef = useRef(false)
  const handleForwardConfirm = useCallback(async (targetSessionId: string, position: 'after' | 'before', summarize: boolean) => {
    const fm = useUIStore.getState().forwardMode
    if (!fm.active || !fm.sourceSessionId || !sessionId) return

    try {
      const msgs = useChatStore.getState().messagesBySession[sessionId] || []
      
      if (fm.mode === 'forward') {
        const sorted = [...fm.sourceIndices].sort((a, b) => a - b)
        const selectedMsgs = sorted.map(i => msgs[i]).filter(Boolean)
        if (selectedMsgs.length === 0) return
        const lastSelected = selectedMsgs[selectedMsgs.length - 1]
        // Use seq numbers, not indices
        const fromSeq = selectedMsgs[0].seq ?? sorted[0] + 1
        const toSeq = lastSelected.role === 'user' && selectedMsgs.length > 1
          ? selectedMsgs[selectedMsgs.length - 2].seq ?? sorted[sorted.length - 2] + 1
          : lastSelected.seq ?? sorted[sorted.length - 1] + 1

        await synthApi.mergeMessages(targetSessionId, sessionId, { sourceRange: [fromSeq, toSeq], position })

        if (lastSelected.role === 'user') {
          useChatStore.getState().setRetryInput(targetSessionId, lastSelected.content)
        }
        notify({ text: 'Сообщения скопированы', type: 'success', category: 'message', sessionId })
      } else {
        await synthApi.addSessionReference(targetSessionId, sessionId, {
          messageRange: [1, sessionMessages.length || 9999],
          position,
          autoSummarize: summarize,
        })
        notify({ text: 'Ссылка на сессию добавлена', type: 'success', category: 'message', sessionId })
      }
      useUIStore.getState().cancelForwardMode()
    } catch (e) {
      notify({ text: `Ошибка: ${(e as Error).message}`, type: 'error', category: 'error', sessionId })
    }
  }, [sessionId, sessionMessages.length, notify])

  // Register the callback so LeftPanel can call it
  useUIStore.getState().forwardConfirmAction = handleForwardConfirm
  if (!forwardCallbackRef.current) {
    forwardCallbackRef.current = true
  }

  if (isMobile) {
    const canSeeLogs = !!user?.roles?.some((r) => r === 'ADMIN' || r === 'AUDITOR')
    const iconBtn = 'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-interactive-hover hover:text-foreground'
    return (
      <header className="flex h-11 shrink-0 items-center gap-1 border-b border-border/70 bg-background px-2">
        <button
          onClick={() => setMobileLeftOpen(!mobileLeftOpen)}
          className={iconBtn}
          title={t('header.showLeftPanel')}
          aria-label={t('header.showLeftPanel')}
        >
          <Icon name="layout-left" className="h-5 w-5" />
        </button>
        <span className="flex min-w-0 flex-1 items-center gap-1.5 text-sm font-semibold text-foreground">
          {headerIcon && (
            <Icon name={headerIcon} className="size-4 shrink-0" style={headerIconColor ? { color: headerIconColor } : undefined} />
          )}
          <span className="truncate">{headerTitle}</span>
        </span>
        <div className="flex shrink-0 items-center gap-0.5">
          <GlobalTransportIndicator />
          {deviceType === 'tablet' && <HybridStatusIndicator />}
          {worktreeName && sessionWorktree && (
            <button
              onClick={handleWorktreeClick}
              className="flex h-9 max-w-[9rem] items-center gap-1 rounded-lg px-1.5 text-muted-foreground transition-colors hover:bg-interactive-hover hover:text-foreground"
              title={t('worktrees.header.badgeTitle')}
              aria-label={t('worktrees.header.badge', { name: worktreeName, branch: sessionWorktree.branch })}
              data-testid="header-worktree-indicator"
            >
              <Icon name="git-branch" className="size-4 shrink-0" />
              <span className="truncate text-[0.625rem]">
                {t('worktrees.header.badge', { name: worktreeName, branch: sessionWorktree.branch })}
              </span>
            </button>
          )}
          <NotificationPopup />
          <AnalyticsButton />
          <PwaInstallButton />
          {isChatTab && <ChatViewControls variant="mobile" />}
          <QuickSettingsPopup
            onSettingsClick={onSettingsClick}
            onAnalyticsClick={() => setAnalyticsDialogOpen(true)}
          />
          {deviceType === 'tablet' && canSeeLogs && (
            <button
              onClick={() => setLogsDialogOpen(true)}
              className={iconBtn}
              title={t('header.logs')}
              aria-label={t('header.logs')}
            >
              <Icon name="file-list" className="h-5 w-5" />
            </button>
          )}
          {deviceType === 'tablet' && (
            <button
              onClick={() => setSettingsDialogOpen(true)}
              className={iconBtn}
              title={t('header.settings')}
              aria-label={t('header.settings')}
            >
              <Icon name="settings-3" className="h-5 w-5" />
            </button>
          )}
          <button
            onClick={() => setMobileRightOpen(!mobileRightOpen)}
            className={iconBtn}
            title={t('header.showRightPanel')}
            aria-label={t('header.showRightPanel')}
          >
            <Icon name="layout-right" className="h-5 w-5" />
          </button>
        </div>
      </header>
    )
  }

  return (
    <header className="flex h-9 items-center justify-between px-3 bg-background">
      <div className="flex items-center gap-3 min-w-0">
        {!leftPanel.isOpen && (
          <button
            onClick={toggleLeftPanel}
            className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-interactive-hover hover:text-foreground"
            title={t('header.showLeftPanel')}
          >
            <Icon name="layout-left" className="h-5 w-5" />
          </button>
        )}

        <span className="flex items-center gap-1.5 font-semibold text-foreground shrink-0">
          {headerIcon && (
            <Icon name={headerIcon} className="size-4 shrink-0" style={headerIconColor ? { color: headerIconColor } : undefined} />
          )}
          {headerTitle}
        </span>

        {/* Chat context — always visible */}
        {isChatTab && (
          <>
            <div className="flex items-center gap-1 ml-1">
              <div
                className="flex items-center gap-1"
                title={contextTitle}
                data-testid="header-context"
                data-level={contextLevel}
              >
                <div className="h-1.5 w-12 rounded-full bg-foreground/10 overflow-hidden">
                  <div
                    className={cn('h-1.5 rounded-full transition-all', contextLevelClasses.bar)}
                    style={{ width: `${windowPercent}%` }}
                  />
                </div>
                <span className={cn('text-[0.625rem] tabular-nums device-hide-label', contextLevelClasses.text)}>
                  {windowPercent}%
                </span>
              </div>
              {!isSubagentSession && (
                <button
                  onClick={handleSummarize}
                  disabled={!canSummarize || summarizing}
                  title={canSummarize ? t('rightPanel.context.summarizeNow') : t('rightPanel.context.notEnoughMessages')}
                  className={cn(
                    'shrink-0 rounded p-1 transition-colors',
                    canSummarize && !summarizing
                      ? 'text-muted-foreground hover:text-foreground hover:bg-interactive-hover'
                      : 'text-muted-foreground opacity-30 cursor-default',
                  )}
                >
                  <Icon name="subtract" className="size-3.5 text-status-warning" />
                </button>
              )}
              {!isSubagentSession && (
                <button
                  onClick={handleClearSession}
                  title={t('leftPanel.clearSession')}
                  className="shrink-0 rounded p-1 text-muted-foreground hover:text-foreground hover:bg-interactive-hover transition-colors"
                >
                  <Icon name="close" className="size-3.5 text-destructive" />
                </button>
              )}

              {/* Selection toggle — always visible */}
              {!selectionMode && (
                <button
                  onClick={handleToggleSelectionMode}
                  className="shrink-0 rounded p-1 text-muted-foreground hover:text-foreground hover:bg-interactive-hover transition-colors"
                  title="Режим выделения"
                >
                  <Icon name="checkbox-multiple" className="size-3.5" />
                </button>
              )}

              {/* Быстрый доступ к виду и плотности ленты */}
              <ChatViewControls />
            </div>

            {/* Selection toolbar */}
            {selectionMode && (
              <div className="flex items-center gap-1 ml-1">
                <button
                  onClick={handleToggleSelectionMode}
                  className="rounded px-2 py-0.5 text-[0.6875rem] font-medium text-primary hover:bg-primary/10"
                >
                  ✕ Выйти
                </button>
                <button
                  onClick={handleSelectAll}
                  className="rounded px-2 py-0.5 text-[0.6875rem] text-muted-foreground hover:text-foreground hover:bg-interactive-hover"
                >
                  Выделить всё
                </button>
                <button
                  onClick={handleClearSelection}
                  className="rounded px-2 py-0.5 text-[0.6875rem] text-muted-foreground hover:text-foreground hover:bg-interactive-hover"
                >
                  Снять
                </button>
                <span className="text-[0.6875rem] text-muted-foreground mx-0.5">
                  {selectedCount}/{totalMessageCount}
                </span>
                <button
                  onClick={handleBulkPin}
                  disabled={selectedCount === 0}
                  className="rounded px-1 py-0.5 text-[0.6875rem] text-muted-foreground hover:text-foreground hover:bg-interactive-hover disabled:opacity-30"
                  title="Закрепить"
                >
                  📌
                </button>
                <button
                  onClick={handleBulkToggle}
                  disabled={selectedCount === 0}
                  className="rounded px-1 py-0.5 text-[0.6875rem] text-muted-foreground hover:text-foreground hover:bg-interactive-hover disabled:opacity-30"
                  title="Отключить"
                >
                  👁
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={selectedCount === 0}
                  className="rounded px-1 py-0.5 text-[0.6875rem] text-muted-foreground hover:text-destructive hover:bg-destructive/10 disabled:opacity-30"
                  title="Удалить"
                >
                  🗑
                </button>
                <button
                  onClick={handleForward}
                  disabled={selectedCount === 0}
                  className="rounded px-2 py-0.5 text-[0.6875rem] text-muted-foreground hover:text-foreground hover:bg-interactive-hover disabled:opacity-30"
                  title="Переслать"
                >
                  ↗ Переслать
                </button>
              </div>
            )}

            {/* Merge button — always visible, independent of selection */}
            <button
              onClick={handleMerge}
              className="shrink-0 rounded px-2 py-0.5 text-[0.6875rem] text-muted-foreground hover:text-foreground hover:bg-interactive-hover transition-colors ml-1"
              title="Слияние"
            >
              🔗 <span className="device-hide-label">Слияние</span>
            </button>
          </>
        )}

        {/* File viewer toolbar */}
        {isFileViewer && activeTab && (
          <div className="flex items-center gap-1 ml-1">
            <span className="text-xs font-mono text-muted-foreground truncate max-w-[16.25rem]">
              {activeTab.data.filePath}
            </span>
            {canEditViewedFile && (
              <button
                onClick={handleFileEdit}
                className="shrink-0 rounded p-1 text-muted-foreground hover:text-foreground hover:bg-interactive-hover transition-colors"
                title={t('file.edit')}
              >
                <Icon name="edit-2" className="size-3.5" />
              </button>
            )}
            <button
              onClick={handleFileDownload}
              className="shrink-0 rounded p-1 text-muted-foreground hover:text-foreground hover:bg-interactive-hover transition-colors"
              title={t('file.download')}
            >
              <Icon name="download" className="size-3.5" />
            </button>
          </div>
        )}

        {/* File editor toolbar */}
        {isFileEditor && activeTab && (
          <div className="flex items-center gap-1 ml-1">
            <span className="text-xs font-mono text-muted-foreground truncate max-w-[16.25rem]">
              {activeTab.data.filePath}
            </span>
            <span className={cn(
              'text-xs',
              editingState === 'saving' && 'text-status-warning',
              editingState === 'saved' && 'text-status-success',
              editingState === 'error' && 'text-status-error',
              isEditorDirty && 'text-status-warning',
              (!editingState || editingState === 'idle') && 'text-muted-foreground',
            )}>
              {editingState === 'saving' ? t('file.saving')
                : editingState === 'saved' ? t('file.saved')
                : editingState === 'error' ? t('file.saveError')
                : isEditorDirty ? t('common.modified')
                : ''}
            </span>
            <button
              onClick={handleFileSave}
              disabled={!isEditorDirty || isSaving}
              className={cn(
                'shrink-0 inline-flex items-center gap-1 rounded px-2 py-0.5 text-[0.6875rem] font-medium transition-colors',
                isEditorDirty && !isSaving
                  ? 'text-primary hover:bg-primary/10'
                  : 'text-muted-foreground opacity-50 cursor-default',
              )}
              title={t('common.save')}
            >
              <Icon name="save" className="size-3.5" />
              {isSaving ? t('file.saving') : t('common.save')}
            </button>
            <button
              onClick={handleFileClose}
              className="shrink-0 rounded p-1 text-muted-foreground hover:text-foreground hover:bg-interactive-hover transition-colors"
              title={t('common.close')}
            >
              <Icon name="x" className="size-3.5" />
            </button>
          </div>
        )}

        {/* Commit detail toolbar */}
        {isCommitDetail && activeTab && (
          <div className="flex items-center gap-1 ml-1">
            <code className="text-[0.625rem] font-mono text-muted-foreground bg-interactive rounded px-1.5 py-px">
              {activeTab.data.commitHash?.slice(0, 7)}
            </code>
            <button
              onClick={handleCommitCopy}
              className="shrink-0 rounded p-1 text-muted-foreground hover:text-foreground hover:bg-interactive-hover transition-colors"
              title={t('commit.copy')}
            >
              <Icon name="file-copy" className="size-3.5" />
            </button>
            <button
              onClick={handleCommitClose}
              className="shrink-0 rounded p-1 text-muted-foreground hover:text-foreground hover:bg-interactive-hover transition-colors"
              title={t('common.close')}
            >
              <Icon name="x" className="size-3.5" />
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <GlobalTransportIndicator />
        <HybridStatusIndicator />
        {worktreeName && sessionWorktree && (
          <button
            onClick={handleWorktreeClick}
            className="flex max-w-[14rem] items-center gap-1 rounded px-1.5 py-0.5 text-muted-foreground transition-colors hover:bg-interactive-hover hover:text-foreground"
            title={t('worktrees.header.badgeTitle')}
            aria-label={t('worktrees.header.badge', { name: worktreeName, branch: sessionWorktree.branch })}
            data-testid="header-worktree-indicator"
          >
            <Icon name="git-branch" className="size-3.5 shrink-0" />
            <span className="truncate text-[0.625rem]">
              {t('worktrees.header.badge', { name: worktreeName, branch: sessionWorktree.branch })}
            </span>
          </button>
        )}
        <NotificationPopup />
        <AnalyticsButton />
        <QuickSettingsPopup
          onSettingsClick={onSettingsClick}
          onAnalyticsClick={() => setAnalyticsDialogOpen(true)}
        />

        {(user?.roles?.includes('ADMIN') || user?.roles?.includes('AUDITOR')) && (
          <button
            onClick={() => setLogsDialogOpen(true)}
            className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-interactive-hover hover:text-foreground"
            title={t('header.logs')}
          >
            <Icon name="file-list" className="h-5 w-5" />
          </button>
        )}

        <button
          onClick={() => setSettingsDialogOpen(true)}
          className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-interactive-hover hover:text-foreground"
          title={t('header.settings')}
        >
          <Icon name="settings-3" className="h-5 w-5" />
        </button>

        {!rightPanel.isOpen && (
          <button
            onClick={toggleRightPanel}
            className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-interactive-hover hover:text-foreground"
            title={t('header.showRightPanel')}
          >
            <Icon name="layout-right" className="h-5 w-5" />
          </button>
        )}
      </div>
    </header>
  )
}
